/**
 * Русский домен AiSearch.
 *
 * `aisearch.ru` (и стенд `2026.aisearch.ru`, который nginx проксирует на
 * русскую версию) — не «ещё один язык того же сайта», а отдельная витрина
 * для российского посетителя. Она показывает ровно одну языковую версию.
 *
 * Почему не оставить переключатель. Человек, пришедший на .ru, ждёт
 * русский сайт; ссылки на испанский и сербский там выглядят мусором и
 * уводят на страницы, которые ему не нужны. Для поиска это хуже вдвойне:
 * один и тот же текст оказывался бы доступен по двум доменам, .ru и
 * .tech, и поисковик сам решал бы, какой из них считать основным.
 *
 * Поэтому домен .ru отвечает только по-русски, а многоязычие живёт на
 * .tech. Проверка вынесена сюда, а не повторяется в middleware и в
 * подвале: правило одно, и расходиться этим двум местам нельзя.
 */

/** Домен запроса обслуживает только русскую версию. */
export function tolkoRusskij(host: string | null | undefined): boolean {
  if (!host) return false;

  const hostname = host.split(':')[0]?.toLowerCase() ?? '';
  // Поддомены тоже: стенд 2026.aisearch.ru и будущие witryny вида
  // www.aisearch.ru обслуживают тот же русский сайт.
  return hostname === 'aisearch.ru' || hostname.endsWith('.aisearch.ru');
}

/** Язык, на котором отвечает такой домен. */
export const RU_DOMEN_LOCALE = 'ru';

/**
 * Заголовок, которым middleware сообщает странице, что запрос пришёл с
 * русского домена.
 *
 * Сама страница хост не читает: разбор домена должен быть в одном месте,
 * иначе правило «что считать русским доменом» разойдётся между
 * middleware и разметкой, и на .ru появятся ссылки на .tech.
 */
export const RU_DOMEN_HEADER = 'x-ais-ru';

/** Адрес русской витрины: свой домен, а не раздел .tech. */
export const AIS_RU_SITE_URL = 'https://aisearch.ru';

/**
 * Ветка русской витрины в дереве маршрутов.
 *
 * Почему отдельная ветка, а не признак внутри страницы. Страницы витрины
 * собираются заранее, один раз на сборку образа, и домена в этот момент
 * не существует: заголовка `Host` нет, потому что нет запроса. Любая
 * проверка домена внутри страницы даёт при сборке «не русский», и этот
 * ответ впечатывается в готовый HTML: один и тот же файл уезжает на оба
 * домена, с почтой и canonical чужого.
 *
 * Ветка решает это тем, что домен становится частью адреса сборки.
 * Собираются обе витрины, каждая со своими адресами, а middleware по
 * хосту выбирает готовую. Страницы остаются статическими.
 *
 * Наружу ветка не видна никогда: в браузере адрес остаётся
 * `aisearch.ru/audio-to-text`.
 */
export const AIS_RU_SEGMENT = 'aisearch-ru';

/** Значение сегмента `[vitrina]` для международного домена. */
export const AIS_TECH_SEGMENT = 'aisearch-tech';

/** Обе витрины: по ним `generateStaticParams` собирает оба варианта. */
export const AIS_VITRINY = [AIS_TECH_SEGMENT, AIS_RU_SEGMENT] as const;

/** Русская ли это витрина. Читается из параметра маршрута, а не из хоста. */
export function ruVitrina(vitrina: string | undefined): boolean {
  return vitrina === AIS_RU_SEGMENT;
}

/**
 * Пришёл ли запрос с русского домена: для серверной отрисовки.
 *
 * Читает заголовок, выставленный middleware, а не хост: так правило
 * остаётся в одном месте, и разметка не начнёт считать русским доменом
 * что-то своё.
 */
export async function ruDomenZaprosa(): Promise<boolean> {
  try {
    // Импорт внутри функции: модуль читают и middleware, и клиентские
    // компоненты, а `next/headers` есть только на сервере страниц.
    const { headers } = await import('next/headers');
    const store = await headers();
    return store.get(RU_DOMEN_HEADER) === '1';
  } catch (oshibka) {
    // Обращением к заголовкам страница объявляет себя зависящей от
    // запроса, и Next сообщает об этом исключением. Гасить его нельзя:
    // именно так страница осталась заранее собранной, признак домена
    // всегда был ложью, и на aisearch.ru уезжали canonical и почта
    // чужого домена. Ошибка обязана дойти до Next.
    if (bailoutNexta(oshibka)) throw oshibka;

    // Остальное — вызов вне запроса вообще (юнит-тест). Там домена нет,
    // и «не русский» здесь честный ответ, а не подмена.
    return false;
  }
}

/**
 * Это Next просит отрисовывать страницу на каждый запрос, а не сбой.
 *
 * Признак — `digest` вида `DYNAMIC_SERVER_USAGE`, а также служебные
 * объекты отложенной отрисовки, которые приходят тем же путём.
 */
function bailoutNexta(oshibka: unknown): boolean {
  if (typeof oshibka !== 'object' || oshibka === null) return false;

  const digest = (oshibka as { digest?: unknown }).digest;
  if (typeof digest === 'string' && digest.startsWith('DYNAMIC_SERVER_USAGE')) return true;

  const imya = (oshibka as { name?: unknown }).name;
  return typeof imya === 'string' && /Bailout|Postpone|DynamicServer/i.test(imya);
}
